import React, {useEffect, useRef} from 'react';

import type {User} from '../../pages/reportBuilder/constants/ReportBuilderInterfce';

interface MentionListProps {
  users: User[];
  onSelect: (user: User) => void;
  onClose: () => void;
  position: {top: number; left: number};
  activeIndex?: number;
  onReachEnd?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  hasSearched?: boolean;
}

export const MentionList: React.FC<MentionListProps> = ({
  users,
  onSelect,
  onClose,
  position,
  activeIndex = 0,
  onReachEnd,
  hasMore = false,
  isLoading = false,
  hasSearched = false
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    if (!users.length) return;
    const safeIndex = Math.max(0, Math.min(activeIndex, users.length - 1));
    const activeItem = itemRefs.current[safeIndex];
    if (!activeItem) return;
    activeItem.scrollIntoView({block: 'nearest'});
  }, [activeIndex, users.length]);

  const showEmptyState = users.length === 0 && !isLoading && hasSearched;

  return (
    <div
      ref={listRef}
      className="comment-mention-list"
      role="listbox"
      aria-label={showEmptyState ? 'No mention suggestions' : 'Mention users'}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left
      }}
      onScroll={(event) => {
        const el = event.currentTarget;
        const isNearBottom =
          el.scrollTop + el.clientHeight >= el.scrollHeight - 16;
        if (isNearBottom) {
          onReachEnd?.();
        }
      }}
    >
      {showEmptyState ? (
        <div className="comment-mention-list__empty" role="status">
          No suggestions found
        </div>
      ) : (
        users.map((user, index) => (
          <div
            key={user.id}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            role="option"
            aria-selected={index === activeIndex}
            className={`comment-mention-list__item${
              index === activeIndex ? ' comment-mention-list__item--active' : ''
            }`}
            onClick={() => {
              onSelect(user);
            }}
          >
            <span
              className="comment-mention-list__avatar"
              style={{backgroundColor: user.color}}
            >
              {user.avatar}
            </span>
            <span className="comment-mention-list__meta">
              <span className="comment-mention-list__name">{user.name}</span>
              {user.email ? (
                <span className="comment-mention-list__email">{user.email}</span>
              ) : null}
            </span>
          </div>
        ))
      )}
      {(isLoading || hasMore) && (
        <div className="comment-mention-list__footer">
          {isLoading
            ? users.length === 0
              ? 'Searching…'
              : 'Loading more users…'
            : 'Scroll to load more'}
        </div>
      )}
    </div>
  );
};
